// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Fingerprint, BookMarked, Mail } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { startAuthentication } from "@simplewebauthn/browser";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicConsent, setMagicConsent] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const sendMagicLink = async (e) => {
    e?.preventDefault?.();
    if (!magicLinkEmail.trim()) { toast.error("Enter your email first"); return; }
    if (!magicConsent) { toast.error("Please tick the box to agree to the Terms and Privacy Policy."); return; }
    setMagicBusy(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const redirect_path = params.get("next") || "/dashboard";
      await api.post("/auth/magic-link/request", {
        email: magicLinkEmail.trim().toLowerCase(),
        redirect_path,
      });
      setMagicSent(true);
      toast.success("Sign-in link sent — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send sign-in link. Try again or use Google.");
    } finally { setMagicBusy(false); }
  };

  const googleSignIn = () => {
    const params = new URLSearchParams(window.location.search);
    const next = params.get("next") || "/dashboard";
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const passkeySignIn = async (opts_email = null) => {
    // Email-less first (discoverable passkeys). If the browser has no resident
    // passkey for this site, fall back to typing an email.
    setBusy(true);
    try {
      const body = opts_email && opts_email.trim() ? { email: opts_email.trim().toLowerCase() } : {};
      const opts = await api.post("/webauthn/login/options", body);
      let assertion;
      try {
        assertion = await startAuthentication({ optionsJSON: opts.data.options });
      } catch (browserErr) {
        const name = browserErr?.name || "";
        if (name === "NotAllowedError" || name === "AbortError") {
          if (!opts_email || !opts_email.trim()) {
            toast.message("No passkey saved on this device. Type your email below and try again, or use Google.");
          } else {
            toast.error("Passkey prompt was cancelled. Tap again and approve with Face ID / Touch ID.");
          }
        } else if (name === "InvalidStateError") {
          toast.error("This device has no matching passkey. Sign in with Google, then add a new passkey from Settings.");
        } else {
          toast.error(browserErr?.message || "Passkey not available on this device.");
        }
        return;
      }
      const r = await api.post("/webauthn/login/verify", { credential: assertion, challenge_id: opts.data.challenge_id });
      setUser(r.data);
      toast.success("Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      if (status === 404 || /no passkey|isn'?t registered|not registered/i.test(detail)) {
        toast.error(
          "No passkey on file for this account yet. Sign in with Google once, then add a passkey from Settings → Passkeys.",
          { duration: 8000 }
        );
      } else {
        toast.error(detail || "Passkey sign-in failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] grid lg:grid-cols-2">
      <div className="hidden lg:flex items-center justify-center p-12 bg-yellow-100 border-r-2 border-slate-900">
        <div className="max-w-md">
          <div className="w-14 h-14 bg-white border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>
            <BookMarked className="w-7 h-7" strokeWidth={2.5}/>
          </div>
          <h1 className="font-display text-5xl font-black mt-6 leading-tight">Welcome back to Studict</h1>
          <p className="text-lg font-medium text-slate-700 mt-4">Sign in to access your glossary, scans and study materials.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <h2 className="font-display text-3xl font-black">Sign in</h2>
          <p className="text-sm font-medium text-slate-600 mt-2">Use Google to start. Add a passkey from settings for faster logins next time.</p>

          <button onClick={googleSignIn} data-testid="google-signin-btn" className="nb-btn-primary w-full mt-8 inline-flex items-center justify-center gap-3">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.13 4.13 0 0 1-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.86 2.69-6.61z"/><path fill="#34A853" d="M9 18c2.43 0 4.46-.81 5.95-2.18l-2.9-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.32-1.58-5.03-3.71H1v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H1A9 9 0 0 0 0 9c0 1.45.35 2.83.97 4.04l2.97-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 1 4.96l2.97 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>
            Continue with Google
          </button>
          <p className="text-xs text-slate-500 mt-2 text-center" data-testid="google-consent-line">
            By continuing you confirm you're <strong>13 or older</strong> and agree to our{" "}
            <Link to="/terms" className="underline font-bold hover:text-slate-900">Terms</Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline font-bold hover:text-slate-900">Privacy Policy</Link>.
          </p>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-0.5 bg-slate-300"/>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">or use email</span>
            <div className="flex-1 h-0.5 bg-slate-300"/>
          </div>

          {magicSent ? (
            <div className="nb-card-sm p-4 bg-emerald-50 text-center" data-testid="magic-link-sent-card">
              <Mail className="w-6 h-6 mx-auto" strokeWidth={2.5}/>
              <p className="font-bold mt-2 text-sm">Check your email</p>
              <p className="text-xs text-slate-600 mt-1">We sent a one-time sign-in link to <strong>{magicLinkEmail}</strong>. It works for 15 minutes.</p>
              <button onClick={() => { setMagicSent(false); }} data-testid="magic-link-resend" className="mt-3 text-xs font-bold text-slate-500 underline hover:text-slate-900">
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={sendMagicLink}>
              <label className="block text-xs uppercase tracking-widest font-bold mb-2">Your email (any provider)</label>
              <input
                type="email"
                data-testid="magic-link-email-input"
                value={magicLinkEmail}
                onChange={(e) => setMagicLinkEmail(e.target.value)}
                placeholder="you@hotmail.com"
                className="nb-input"
                autoComplete="email"
              />
              <button type="submit" disabled={magicBusy} data-testid="magic-link-send-btn" className="nb-btn-secondary w-full mt-3 inline-flex items-center justify-center gap-3">
                <Mail className="w-5 h-5" strokeWidth={2.5}/>
                {magicBusy ? "Sending…" : "Email me a sign-in link"}
              </button>
              <label className="flex items-start gap-2 mt-3 text-xs text-slate-600 cursor-pointer select-none" data-testid="magic-link-consent">
                <input
                  type="checkbox"
                  data-testid="magic-link-consent-checkbox"
                  checked={magicConsent}
                  onChange={(e) => setMagicConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 border-2 border-slate-900 rounded shrink-0"
                />
                <span>
                  I'm <strong>13 or older</strong> and I agree to the{" "}
                  <Link to="/terms" className="underline font-bold">Terms</Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline font-bold">Privacy Policy</Link>.
                </span>
              </label>
              <p className="text-xs text-slate-500 mt-2 text-center">No password. Works with Hotmail, Outlook, Yahoo, school emails — anything.</p>
            </form>
          )}

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-0.5 bg-slate-300"/>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">or use a passkey</span>
            <div className="flex-1 h-0.5 bg-slate-300"/>
          </div>

          <button onClick={() => passkeySignIn(null)} disabled={busy} data-testid="passkey-signin-btn" className="nb-btn-secondary w-full inline-flex items-center justify-center gap-3">
            <Fingerprint className="w-5 h-5" strokeWidth={2.5}/>
            {busy ? "Verifying…" : "Sign in with passkey"}
          </button>
          <p className="text-xs text-slate-500 mt-2 text-center">Tap and your device will offer your saved Studict passkey.</p>

          <details className="mt-4" data-testid="passkey-email-fallback">
            <summary className="text-xs font-bold text-slate-500 cursor-pointer hover:text-slate-900">No passkey prompt? Type your email instead</summary>
            <div className="mt-3">
              <label className="block text-xs uppercase tracking-widest font-bold mb-2">Your email</label>
              <input type="email" data-testid="passkey-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" className="nb-input"/>
              <button onClick={() => passkeySignIn(email)} disabled={busy || !email.trim()} data-testid="passkey-signin-with-email-btn" className="nb-btn-secondary w-full mt-3 inline-flex items-center justify-center gap-3">
                <Fingerprint className="w-5 h-5" strokeWidth={2.5}/>
                {busy ? "Verifying…" : "Sign in with passkey (email)"}
              </button>
            </div>
          </details>

          <p className="text-xs font-medium text-slate-500 mt-6">
            Studict is a study companion — not a substitute for a teacher. Read our{" "}
            <Link to="/privacy" className="underline font-bold">Privacy Policy</Link> to see how we keep your data safe.
          </p>
        </div>
      </div>
    </div>
  );
}
